import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { StrictMode, useCallback, useEffect, useState } from "react"
import type { ReactNode } from "react"
import ReactDOM from "react-dom/client"
import { routeTree } from "./routeTree.gen"

import { OpenAPI, OrganizationsService, UsersService } from "./client"
import type { UserPublic } from "./client"
import { LoadingSplash } from "./components/Common/LoadingSplash"
import { CustomProvider } from "./components/ui/provider"
import { isLoggedIn } from "./hooks/useAuth"
import { shouldRedirectToLogin } from "./lib/api-error-utils"

OpenAPI.BASE = import.meta.env.VITE_API_URL
OpenAPI.TOKEN = async () => {
  return localStorage.getItem("access_token") || ""
}

const handleApiError = (error: Error) => {
  if (!shouldRedirectToLogin(error)) {
    return
  }

  // Clear the expired/invalid token
  localStorage.removeItem("access_token")
  sessionStorage.removeItem("access_token")

  // Check if we're in an embedded context (iframe or embed routes)
  const isInIframe = window.self !== window.top
  const isEmbedRoute = window.location.pathname.startsWith("/embed")

  if (isInIframe || isEmbedRoute) {
    // If we have query params that might include signature, reload the page
    // This will trigger the embed auth flow again
    if (window.location.search) {
      window.location.reload()
    } else {
      // No params available, show error message
      window.location.href = `/embed/app?error=session_expired&message=${encodeURIComponent(
        "Your session has expired. Please access this page from the original link.",
      )}`
    }
  } else {
    // Regular login flow for non-embedded contexts
    window.location.href = "/login"
  }
}
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleApiError,
  }),
  mutationCache: new MutationCache({
    onError: handleApiError,
  }),
})

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  // This is important for proper URL handling on refresh
  defaultPreloadStaleTime: 0,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
  interface RouterContext {
    queryClient: QueryClient
  }
}

type AppInitializerProps = {
  children: ReactNode
  queryClient: QueryClient
  onBootstrapError: (error: Error) => void
}

const fetchCurrentUserWithRetry = async (
  queryClient: QueryClient,
  maxRetries = 3,
): Promise<UserPublic> => {
  let lastError: Error | null = null

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Check token exists before attempting
      const token = localStorage.getItem("access_token")
      if (!token) {
        throw new Error("No access token found")
      }

      // Proactively check if token is expired before making request
      try {
        const parts = token.split(".")
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]))
          if (payload.exp) {
            const expirationTime = payload.exp * 1000
            const now = Date.now()
            if (now >= expirationTime) {
              localStorage.removeItem("access_token")
              sessionStorage.removeItem("access_token")
              throw new Error("Token expired (pre-check)")
            }
          }
        }
      } catch (decodeError) {
        // Ignore decode errors, let the request fail if token is invalid
      }

      return await queryClient.fetchQuery<UserPublic>({
        queryKey: ["currentUser"],
        queryFn: UsersService.readUserMe,
      })
    } catch (error) {
      lastError = error as Error

      // Check if this is an expired token error (from backend message)
      const isExpiredTokenError =
        lastError.message.includes("expired") ||
        lastError.message.includes("Signature has expired")

      // Check if we're in an embedded context
      const isInIframe = window.self !== window.top
      const isEmbedRoute = window.location.pathname.startsWith("/embed")
      const isEmbeddedContext = isInIframe || isEmbedRoute

      // In embedded contexts, treat any 403 as potentially expired token
      // (since error details might not propagate from backend)
      const is403Error = error instanceof Error && error.message.includes("403")
      const shouldClearAndReload =
        isExpiredTokenError || (is403Error && isEmbeddedContext)

      if (shouldClearAndReload) {
        // Clear the expired token immediately
        localStorage.removeItem("access_token")
        sessionStorage.removeItem("access_token")
        // Don't retry - throw immediately to trigger handleApiError
        throw error
      }

      // If it's not a 403, don't retry
      if (!is403Error) {
        throw error
      }

      // Only retry for non-embedded 403s (potential transient issues)
      if (attempt < maxRetries - 1 && !isEmbeddedContext) {
        const delay = 3 ** attempt * 100 // 100ms, 300ms, 900ms
        await new Promise((resolve) => setTimeout(resolve, delay))
      } else {
        // Don't retry in embedded contexts - throw immediately
        throw error
      }
    }
  }

  throw lastError || new Error("Failed to fetch current user after retries")
}

function AppInitializer({
  children,
  queryClient,
  onBootstrapError,
}: AppInitializerProps) {
  const [isReady, setIsReady] = useState(false)
  const [bootstrapError, setBootstrapError] = useState<Error | null>(null)

  const waitForWindowLoad = useCallback(() => {
    if (document.readyState === "complete") {
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      const onLoad = () => {
        window.removeEventListener("load", onLoad)
        resolve()
      }

      window.addEventListener("load", onLoad, { once: true })
    })
  }, [])

  const runInitialRequests = useCallback(async () => {
    if (!isLoggedIn()) {
      return
    }

    // Skip bootstrap requests if we're still in embed auth flow
    // This prevents race conditions during authentication
    const isEmbedAuthInProgress = sessionStorage.getItem(
      "embed_auth_in_progress",
    )
    if (isEmbedAuthInProgress === "true") {
      sessionStorage.removeItem("embed_auth_in_progress")
      return
    }

    const user = await fetchCurrentUserWithRetry(queryClient)

    if (user && !user.is_superuser) {
      await queryClient.fetchQuery({
        queryKey: ["myMemberships", user.id],
        queryFn: OrganizationsService.getMyMemberships,
      })
    }
  }, [queryClient, fetchCurrentUserWithRetry])

  const runBootstrap = useCallback(async () => {
    setBootstrapError(null)
    await Promise.all([waitForWindowLoad(), runInitialRequests()])
  }, [runInitialRequests, waitForWindowLoad])

  useEffect(() => {
    let cancelled = false

    const bootstrap = async () => {
      try {
        await runBootstrap()
        if (!cancelled) {
          setIsReady(true)
        }
      } catch (error) {
        if (cancelled) return
        const err = error as Error
        onBootstrapError(err)
        setBootstrapError(err)
      }
    }

    bootstrap()

    return () => {
      cancelled = true
    }
  }, [onBootstrapError, runBootstrap])

  const handleRetry = useCallback(() => {
    setIsReady(false)
    setBootstrapError(null)
    runBootstrap().catch((err) => {
      const error = err as Error
      onBootstrapError(error)
      setBootstrapError(error)
    })
  }, [onBootstrapError, runBootstrap])

  if (!isReady) {
    return (
      <LoadingSplash
        isError={Boolean(bootstrapError)}
        message={
          bootstrapError
            ? "We hit a snag while loading your workspace. Please try again."
            : "Preparing your workspace..."
        }
        onRetry={bootstrapError ? handleRetry : undefined}
      />
    )
  }

  return <>{children}</>
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <CustomProvider>
      <QueryClientProvider client={queryClient}>
        <AppInitializer
          queryClient={queryClient}
          onBootstrapError={handleApiError}
        >
          <RouterProvider router={router} />
        </AppInitializer>
      </QueryClientProvider>
    </CustomProvider>
  </StrictMode>,
)
